import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2C4C3B",
      light: "#4A6B5A",
      dark: "#1E3329",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#C46A4E",
      light: "#D48A6E",
      dark: "#A54A2E",
      contrastText: "#FFFFFF",
    },

    // NEW: neutrals + dividers + actions tuned for accessibility
    background: { default: "#F4EFE8", paper: "#E7E1DA" },
    text: { primary: "#2A2A2A", secondary: "#555555" },
    error: { main: "#d32f2f" },
    success: { main: "#1E3329" },
    warning: { main: "#C47E2D" }, // warm, printable
    info: { main: "#4B6E66" }, // muted teal for tips
    divider: "#DAD4CB",
    border: { main: "#DAD4CB" },

    // Custom roles (JS is fine; TS would need module augmentation)
    neutral: { main: "#9E9A92", contrastText: "#2A2A2A" },
    muted: { main: "#F4EFE8" },

    // Improve hover/focus visibility
    action: {
      hover: "rgba(44,76,59,0.06)",
      selected: "rgba(196,106,78,0.12)",
      focus: "rgba(196,106,78,0.36)",
      active: "rgba(0,0,0,0.54)",
      disabled: "rgba(0,0,0,0.26)",
      disabledBackground: "rgba(0,0,0,0.08)",
    },
  },

  // TYPE: serif + script accent + readable UI sans
  typography: {
    fontFamily: '"Work Sans","Roboto","Helvetica","Arial",sans-serif',
    // Headings in Cormorant for artisanal feel
    h1: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "3rem",
      fontWeight: 600,
      letterSpacing: "0.2px",
    },
    h2: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "2.5rem",
      fontWeight: 600,
      letterSpacing: "0.2px",
    },
    h3: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "2rem",
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Cormorant Garamond",serif',
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Work Sans",sans-serif',
      fontSize: "1rem",
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Work Sans",sans-serif',
      fontSize: "0.9375rem",
      lineHeight: 1.65,
    },
    button: {
      fontFamily: '"Work Sans",sans-serif',
      fontWeight: 600,
      letterSpacing: "0.3px",
    },
    // NEW utility styles you can consume via sx
    subtitleEm: { fontFamily: '"Dancing Script",cursive', fontSize: "1.25rem" },
  },

  spacing: 8,
  shape: { borderRadius: 12 },

  // Softer shadow system for cards/buttons
  shadows: [
    "none",
    "0 2px 8px rgba(0,0,0,0.08)",
    "0 4px 12px rgba(0,0,0,0.10)",
    "0 8px 20px rgba(0,0,0,0.10)",
    ...Array(21).fill("0 0 0 rgba(0,0,0,0.0)"),
  ],

  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },

  components: {
    // Global: background texture + focus ring
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          "--lp-green": "#2C4C3B",
          "--lp-terracotta": "#C46A4E",
          "--lp-cream": "#F4EFE8",
          "--lp-linen": "#E7E1DA",
          "--lp-charcoal": "#2A2A2A",
          "--lp-divider": "#DAD4CB",
        },
        "@font-face": [], // load Dancing Script / Cormorant via <link> in HTML
        body: {
          backgroundColor: "#F4EFE8",
          color: "#2A2A2A",
          backgroundImage: "url(/woven-overlay.png)",
          backgroundSize: "1200px 1200px",
          backgroundAttachment: "fixed",
          backgroundBlendMode: "soft-light",
        },
        "*:focus-visible": {
          outline: "2px solid #C46A4E",
          outlineOffset: "2px",
          borderRadius: "4px",
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true, size: "medium" },
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 20px",
        },
        contained: {
          backgroundColor: "#2C4C3B",
          color: "#FFFFFF",
          "&:hover": { backgroundColor: "#1E3329" },
          "&:disabled": { backgroundColor: "#9E9A92", color: "#FFFFFF" },
        },
        outlined: {
          borderColor: "#C46A4E",
          color: "#C46A4E",
          "&:hover": {
            backgroundColor: "#C46A4E",
            color: "#FFFFFF",
            borderColor: "#C46A4E",
          },
        },
        text: {
          color: "#2C4C3B",
          "&:hover": { backgroundColor: "rgba(44,76,59,0.06)" },
        },
      },
      // NEW variants: link + tonal
      variants: [
        {
          props: { variant: "link" },
          style: {
            padding: 0,
            minWidth: 0,
            color: "#2C4C3B",
            textDecoration: "underline",
            "&:hover": { color: "#C46A4E", background: "transparent" },
          },
        },
        {
          props: { color: "secondary", variant: "contained" },
          style: { color: "#FFFFFF" },
        },
        {
          props: { variant: "tonal" },
          style: {
            background: "rgba(44,76,59,0.12)",
            color: "#1E3329",
            "&:hover": { background: "rgba(44,76,59,0.18)" },
          },
        },
        {
          props: { variant: "stitch-chip" },
          style: {
            all: "unset",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            background: "#E7E1DA", // Linen
            border: "1px solid #DAD4CB", // Warm Grey
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 150ms ease-in-out",
            fontFamily: '"Work Sans", sans-serif',
            position: "relative",
            overflow: "hidden",
            padding: 0,
            margin: 0,
            textTransform: "none",
            fontWeight: "normal",
            height: "34px",
            minWidth: "120px",
            "&:hover": {
              backgroundColor: "#EAF2EE", // Green tint
              transform: "translateY(-1px)",
            },
            "&:focus-visible": {
              outline: "2px solid #C46A4E", // Terracotta
              outlineOffset: "2px",
            },
            "&.stitch-chip--selected": {
              border: "2px solid #C46A4E", // Terracotta
              boxShadow: "inset 0 0 0 1px #FFF", // Subtle inner shadow
            },
            "&:active": {
              transform: "scale(0.99)",
              backgroundColor: "#E0E0E0",
            },
            "&:disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: "#E7E1DA",
                transform: "none",
              },
            },
          },
        },
        {
          props: { variant: "stitch-chip", size: "cozy" },
          style: {
            height: "40px",
            minWidth: "140px",
          },
        },
      ],
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#E7E1DA",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: "url(/woven-overlay.png)",
            backgroundSize: "cover",
            opacity: 0.05,
            pointerEvents: "none",
          },
        },
      },
    },

    // Paper "woven / plain / glass" kept; add padding + banner height controls
    MuiPaper: {
      variants: [
        {
          props: { variant: "woven" },
          style: {
            backgroundColor: "#E7E1DA",
            position: "relative",
            padding: "16px",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: "url(/woven-overlay.png)",
              backgroundSize: "cover",
              opacity: 0.08,
              pointerEvents: "none",
            },
          },
        },
        {
          props: { variant: "plain" },
          style: {
            backgroundColor: "#F4EFE8",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
          },
        },
        {
          props: { variant: "glass" },
          style: {
            backgroundColor: "rgba(244,239,232,0.85)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          },
        },
      ],
    },

    // Form fields tuned for terracotta focus
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: "#FFFFFF",
            "& fieldset": { borderColor: "#DAD4CB" },
            "&:hover fieldset": { borderColor: "#C46A4E" },
            "&.Mui-focused fieldset": {
              borderColor: "#C46A4E",
              borderWidth: 2,
            },
          },
        },
      },
    },

    // Chart / table readability
    MuiTable: { styleOverrides: { root: { backgroundColor: "#FFFFFF" } } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "#DAD4CB",
          fontFamily: '"Roboto Mono", monospace',
          fontSize: "1rem",
          padding: "8px",
          textAlign: "center",
          cursor: "pointer",
        },
        head: { fontWeight: 700, backgroundColor: "#F8F6F2" },
      },
    },

    // AppBar polish
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: "#2C4C3B", color: "#FFFFFF", height: 64 },
      },
    },

    // Chips for pattern labels
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 600 },
        colorPrimary: {
          backgroundColor: "rgba(44,76,59,0.12)",
          color: "#1E3329",
        },
        outlined: { borderColor: "#DAD4CB" },
      },
    },

    // Stitch Chip internal elements
    MuiBox: {
      styleOverrides: {
        root: {
          "&.stitch-chip__glyph": {
            width: "32px", // Increased from 28px
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 6px 4px 8px", // More padding on both sides
            flexShrink: 0,
          },
          "&.stitch-chip__divider": {
            width: "1px",
            height: "12px",
            backgroundColor: "#DAD4CB",
            margin: "0 12px", // Increased from 8px to 12px for even more space
            flexShrink: 0,
          },
          "&.stitch-chip__abbrev": {
            fontFamily: '"Work Sans", sans-serif',
            fontSize: "14px",
            fontWeight: 600,
            color: "#2A2A2A",
            paddingLeft: "16px", // Increased from 12px to 16px for more space
            paddingRight: "8px", // Added right padding so text doesn't touch edge
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          },
          "&.stitch-chip__symbol": {
            fontFamily: '"Roboto Mono", monospace',
            fontSize: "18px",
            fontWeight: "bold",
            color: "#2A2A2A",
            lineHeight: 1,
          },
        },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: "#DAD4CB" } } },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#2C4C3B",
          "&:hover": { backgroundColor: "rgba(44,76,59,0.08)" },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#2C4C3B",
          color: "#FFFFFF",
          fontSize: "0.8125rem",
        },
        arrow: { color: "#2C4C3B" },
      },
    },

    // Links consistent with brand
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#2C4C3B",
          textUnderlineOffset: "2px",
          "&:hover": { color: "#C46A4E" },
        },
      },
    },
  },
});

export default theme;
